// src/store/builders/useProjectStatus.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import { toast } from "react-hot-toast";
import useAuthStore from "../useAuthStore";

const VALID_STATUSES = [
  "pending_approval",
  "pending_review",
  "rejected",
  "approved",
  "active",
  "checking",
  "hold",
  "completion_request",
  "completed",
];

const useProjectStatus = create(
  persist(
    (set, get) => ({
      projects: [],
      isLoading: false,
      error: null,
      selectedProject: null,
      documentStatus: null,
      currentFilter: null,

      fetchProjects: async (status = null, limit = 10, page = 1) => {
        set({ isLoading: true, error: null, currentFilter: status });
        try {
          const { token, user, userType } = useAuthStore.getState();

          if (!token) throw new Error("Authentication token missing!");

          const params = new URLSearchParams({ limit, page });

          if (userType === "builder") {
            if (!user || !user.id) throw new Error("Builder ID missing!");
            params.append("builderId", user.id);
          } else if (userType === "customer") {
            const customerId = user?.customerId;
            if (!customerId) throw new Error("Customer ID missing!");
            params.append("customerId", customerId);
          } else {
            throw new Error("Invalid user type!");
          }

          if (status) params.append("status", status);

          const queryString = params.toString();

          const response = await axiosInstancev1.get(
            `/projects?${queryString}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const fetchedProjects = response.data?.projects;
          if (!Array.isArray(fetchedProjects)) {
            console.error("API returned invalid data:", response.data);
            set({ projects: [], error: "Invalid data format from server." });
            toast.error("Invalid data from server.");
          } else {
            set({ projects: fetchedProjects });
          }

        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message;
          set({ error: errorMessage, projects: [] });
          toast.error(errorMessage);
        } finally {
          set({ isLoading: false });
        }
      },

      fetchProjectById: async (projectId) => {
        set({ isLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          const response = await axiosInstancev1.get(`/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          set({ selectedProject: response.data });
          return response.data;
        } catch (error) {
          const errorMessage = error.response?.data?.message || "Failed to fetch project details";
          set({ error: errorMessage });
          toast.error(errorMessage);
        } finally {
          set({ isLoading: false });
        }
      },

      checkCompletionDocumentsStatus: async (projectId) => {
        set({ isLoading: true, error: null, documentStatus: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");

          const response = await axiosInstancev1.get(
            `/projects/${projectId}/completion-documents-status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            set({ documentStatus: response.data });
            return response.data;
          } else {
            throw new Error(response.data.message || "Failed to fetch document status");
          }
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message;
          set({ error: errorMessage });
          toast.error(errorMessage);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      updateProjectStatus: async (projectId, status, commentOrReason = null) => {
        set({ isLoading: true, error: null });
        try {
          if (!VALID_STATUSES.includes(status)) {
            throw new Error("Invalid status provided");
          }
          if ((status === "hold" || status === "rejected") && !commentOrReason?.trim()) {
            throw new Error(
              `${status === "hold" ? "Hold comment" : "Rejection reason"} is required`
            );
          }

          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");

          const payload = { status };

          if (status === "hold") payload.holdComment = commentOrReason;
          if (status === "rejected") payload.rejectionReason = commentOrReason;

          const response = await axiosInstancev1.put(
            `/projects/${projectId}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (!response.data.success) {
            throw new Error(response.data.message);
          }

          toast.success("Status updated successfully");
          await get().fetchProjects(get().currentFilter || null);

          return { success: true };
        } catch (error) {
          const msg = error.response?.data?.message || error.message;
          set({ error: msg });
          toast.error(msg);
          return { success: false, message: msg };
        } finally {
          set({ isLoading: false });
        }
      },

      uploadCompletionDocuments: async (projectId, documents) => {
        set({ isLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");

          const formData = new FormData();
          if (documents.specificationReport) formData.append("specificationReport", documents.specificationReport);
          if (documents.warrantyDocument) formData.append("warrantyDocument", documents.warrantyDocument);
          if (documents.completionCertificate) formData.append("completionCertificate", documents.completionCertificate);
          if (documents.siteImage) formData.append("siteImage", documents.siteImage);

          const response = await axiosInstancev1.post(
            `/projects/${projectId}/completion-documents`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (!response.data.success) {
            throw new Error(response.data.message);
          }

          return {
            success: true,
            message: "Documents uploaded!",
            updatedDocuments: response.data.documents,
          };
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message;
          set({ error: errorMessage });
          toast.error(errorMessage);
          return { success: false, message: errorMessage };
        } finally {
          set({ isLoading: false });
        }
      },

      /** ✅ NEW: Total Completed Project Budget */
      getTotalCompletedBudget: () => {
        const { projects } = get();
        if (!projects || projects.length === 0) return 0;

        return projects.reduce((total, p) => {
          if (p.status === "completed" && p.project_budget) {
            total += Number(p.project_budget);
          }
          return total;
        }, 0);
      },
    }),
    {
      name: "project-status-storage",
      getStorage: () => localStorage,
      partialize: (state) => {
        const { documentStatus, ...rest } = state;
        return rest;
      },
    }
  )
);

export default useProjectStatus;
